import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import Badge from "../components/ui/Badge";
import {
  MapPin,
  Phone,
  Calendar,
  Star,
  ShoppingBag,
  Package,
  Eye,
  Edit2,
  BadgeCheck,
  ArrowRight,
} from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const { getProducts } = useApp();
  const navigate = useNavigate();
  const products = getProducts(user.id);

  const activeProducts = products.filter((p) => p.status === "active");

  const statsRow = [
    {
      label: "Products",
      value: (user.products?.length ?? 0) || products.length,
      icon: Package,
      color: "text-violet-400",
    },
    {
      label: "Incoming Orders",
      value: user.incomingOrderCount ?? 0,
      icon: ShoppingBag,
      color: "text-emerald-400",
    },
    {
      label: "Categories",
      value: user.nicheCategories?.length ?? 0,
      icon: Eye,
      color: "text-blue-400",
    },
    {
      label: "Rating",
      value: user.rating != null ? user.rating : "—",
      icon: Star,
      color: "text-amber-400",
    },
  ];

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Profile hero card */}
      <div className="card overflow-hidden">
        {/* Cover gradient */}
        <div className="h-28 bg-gradient-to-r from-violet-900/60 via-indigo-900/50 to-purple-900/40 relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wMykiPjxwYXRoIGQ9Ik0zNiAxOGMwLTkuOTQtOC4wNi0xOC0xOC0xOFYwYzkuOTQgMCAxOCA4LjA2IDE4IDE4aDEuNWExOCAxOCAwIDAgMSAxOCAxOGgxLjVjMCA5Ljk0LTguMDYgMTgtMTggMThWNDJjLTkuOTQgMC0xOC04LjA2LTE4LTE4SDI0YTE4IDE4IDAgMCAxLTE4LTE4aHYwIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
        </div>

        <div className="px-6 pb-6">
          {/* Avatar + edit */}
          <div className="flex items-end justify-between -mt-10 mb-5">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-2xl font-bold text-white border-4 border-white dark:border-gray-900 shadow-lg">
                {user.initials}
              </div>
              {user.verified && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-900">
                  <BadgeCheck size={13} className="text-white" />
                </div>
              )}
            </div>
            <button className="btn-secondary">
              <Edit2 size={14} /> Edit Profile
            </button>
          </div>

          {/* Name + bio */}
          <div className="mb-5">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{user.name}</h2>
              {user.plan && <Badge variant={user.plan}>{user.plan} Plan</Badge>}
              {user.verified && (
                <span className="flex items-center gap-1 text-xs text-emerald-400">
                  <BadgeCheck size={13} /> Verified Seller
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-violet-400 mt-0.5">
              {user.business}
            </p>
            {user.bio && (
              <p className="text-sm text-gray-400 mt-2 leading-relaxed max-w-lg">
                {user.bio}
              </p>
            )}
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            {(user.marketCity || user.location) && (
              <span className="flex items-center gap-1.5">
                <MapPin size={13} className="text-gray-600" />
                {user.marketName ? `${user.marketName}, ` : ""}{user.marketCity || user.location}
              </span>
            )}
            {user.phone && (
              <span className="flex items-center gap-1.5">
                <Phone size={13} className="text-gray-600" /> {user.phone}
              </span>
            )}
            {user.joinDate && (
              <span className="flex items-center gap-1.5">
                <Calendar size={13} className="text-gray-600" /> Member since{" "}
                {user.joinDate}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {statsRow.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card p-4 text-center">
            <Icon size={20} className={`${color} mx-auto mb-2`} />
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Product listings */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Listed Products
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {activeProducts.length} active listings
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard/products")}
            className="flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 font-medium transition-colors">
            Manage <ArrowRight size={13} />
          </button>
        </div>

        {activeProducts.length === 0 && (
          <div className="py-10 text-center">
            <Package size={32} className="text-gray-300 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              No active products. Start adding listings!
            </p>
            <button
              onClick={() => navigate("/dashboard/products")}
              className="mt-3 btn-primary">
              <Package size={14} /> Add Product
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {activeProducts.map((p) => (
            <div
              key={p.id}
              className="flex items-start gap-3 p-3.5 bg-gray-100/60 dark:bg-gray-800/40 hover:bg-gray-100 dark:hover:bg-gray-800/70 rounded-xl transition-colors cursor-pointer"
              onClick={() => navigate("/dashboard/products")}>
              <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                <Package size={16} className="text-gray-400 dark:text-gray-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                  {p.name}
                </p>
                <p className="text-xs text-gray-500">
                  {p.category} · ₦{p.price.toLocaleString()}
                </p>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="flex items-center gap-1 text-[10px] text-gray-500">
                    <Eye size={10} /> {p.views}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-gray-500">
                    <ShoppingBag size={10} /> {p.sales}
                  </span>
                  {p.featured && (
                    <span className="text-[10px] text-amber-400 font-semibold">
                      ★ Featured
                    </span>
                  )}
                </div>
              </div>
              <div className="text-xs font-semibold text-emerald-400">
                Active
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
